import React, { useEffect, useRef } from 'react';
import parse from 'style-to-object';
import './new-card-output-button.css';
import {
	getCanvasEntityNewOutputButtonDOMId,
	highlightElementIndefinetly,
	highlightElementSuccess,
	removeHighlightElementIndefinetly,
	removeHighlightElementSuccess,
} from '../../../services/editor/ui';
import { CanvasEntityElement } from '../../../services/editor/CanvasElement';
import {
	DraggableExecutableEntityState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
} from '@elyx-code/project-logic-tree';
import { ConnectionNodeType, EditorService } from '../../../services/editor';

interface Props {
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	canvasElement: CanvasEntityElement;
	entity: DraggableExecutableEntityState;
	project: EditorService;
	size?: 'small' | 'medium' | 'large';
	iconStart?: string;
	iconEnd?: string;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	[key: string]: any;
}

const NewCardOutputButton: React.FC<Props> = ({
	onClick,
	parentDisabledReasons,
	color,
	project,
	entity,
	canvasElement,
	size,
	iconStart,
	iconEnd,
	...otherProps
}) => {
	const buttonRef = useRef<HTMLButtonElement>(null);

	const {
		classes: rootClasses = [],
		style = '',
		...remainingProps
	} = otherProps || {};

	// Resolve custom color if it isn't one of the theme colors
	const customColor = ![
		'primary',
		'secondary',
		'error',
		'warning',
		'success',
		'grey',
	].includes(color)
		? color
		: undefined;

	const stylesString = style + (customColor ? `color: ${customColor};` : '');

	const parentOutOfScope = parentDisabledReasons.includes(
		CanvasEntityConnectionDisabledReason.NotInScope
	);

	useEffect(() => {
		const handleConnectionStart = (payload: any) => {
			if (
				!parentOutOfScope &&
				[ConnectionNodeType.PropertyIn].includes(payload.nodeType)
			) {
				if (buttonRef.current) {
					highlightElementIndefinetly(buttonRef.current);
				}
			}
		};

		const handleConnectionEnd = () => {
			if (buttonRef.current) {
				removeHighlightElementIndefinetly(buttonRef.current);
				removeHighlightElementSuccess(buttonRef.current);
			}
		};

		project.on('canvas-connection-start', handleConnectionStart);
		project.on('canvas-connection-end', handleConnectionEnd);

		return () => {
			project.off('canvas-connection-start', handleConnectionStart);
			project.off('canvas-connection-end', handleConnectionEnd);

			// Also clean up any lingering highlights just in case
			if (buttonRef.current) {
				removeHighlightElementIndefinetly(buttonRef.current);
				removeHighlightElementSuccess(buttonRef.current);
			}
		};
	}, [
		project,
		parentOutOfScope,
		buttonRef,
		highlightElementIndefinetly,
		removeHighlightElementIndefinetly,
		removeHighlightElementSuccess,
	]);

	return (
		<button
			ref={buttonRef}
			className={[
				'new-card-output-button',
				...(!customColor
					? [`new-card-output-button__${color ?? 'primary'}`]
					: []),
				...rootClasses,
			].join(' ')}
			id={getCanvasEntityNewOutputButtonDOMId(entity)}
			style={parse(stylesString) || undefined}
			onMouseOver={(e: any) => {
				if (!!project.canvas?.activeConnectionDrawing && !parentOutOfScope) {
					if (
						project.onGoingConnectionTarget &&
						[ConnectionNodeType.PropertyIn].includes(
							project.onGoingConnectionNodeType
						)
					) {
						project.onGoingConnectionSource = entity;
						project.onGoingConnectionSourceId =
							getCanvasEntityNewOutputButtonDOMId(entity);

						project.canvas?.activeConnectionDrawing.snap(
							getCanvasEntityNewOutputButtonDOMId(entity)
						);

						removeHighlightElementIndefinetly(buttonRef.current);
						highlightElementSuccess(buttonRef.current);
					}
				}

				remainingProps.onMouseOver?.(e);
			}}
			onMouseLeave={(e: any) => {
				if (
					!!project.canvas?.activeConnectionDrawing &&
					!parentOutOfScope &&
					[ConnectionNodeType.PropertyIn].includes(
						project.onGoingConnectionNodeType
					)
				) {
					removeHighlightElementIndefinetly(buttonRef.current);
					highlightElementIndefinetly(buttonRef.current);

					if (
						project.canvas?.activeConnectionDrawing?.snappedTo ===
						getCanvasEntityNewOutputButtonDOMId(entity)
					) {
						project.canvas?.activeConnectionDrawing?.unSnap();
					}

					if (
						project.onGoingConnectionSource === entity &&
						project.onGoingConnectionSourceId ===
							getCanvasEntityNewOutputButtonDOMId(entity) &&
						[ConnectionNodeType.PropertyIn].includes(
							project.onGoingConnectionNodeType
						)
					) {
						project.onGoingConnectionSource = null;
						project.onGoingConnectionSourceId = null;
					}
				}

				remainingProps.onMouseLeave?.(e);
			}}
			onClick={onClick}
		>
			<div className='new-card-output-button__background' />
			<div className='new-card-output-button__content'>
				<div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
					{iconStart && <i className={`${iconStart}`} />}
					{otherProps.children}
					{iconEnd && <i className={`${iconEnd}`} />}
				</div>
			</div>
		</button>
	);
};

export default NewCardOutputButton;
